import { useEffect, useState } from "react";
import { api, downloadFile } from "../lib/api";
import { toast } from "sonner";
import {
  ShieldCheck, AlertTriangle, Sparkles, RefreshCw, Wand2, Download,
  Save, Info, CheckCircle2, ArrowUp
} from "lucide-react";
import MarkdownPreview from "../components/MarkdownPreview";
import { UpgradeAd } from "../components/PremiumGate";
import { useAuth } from "../lib/AuthContext";

export default function ATSReview() {
  const { user } = useAuth();
  const isPremium = user?.subscription === "premium" || user?.is_admin;
  const [resumes, setResumes] = useState([]);
  const [selected, setSelected] = useState("");
  const [busy, setBusy] = useState(false);
  const [data, setData] = useState(null);

  // Improved resume state
  const [improving, setImproving] = useState(false);
  const [improved, setImproved] = useState(null); // {improved_resume_markdown, estimated_score, changes_summary}
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/resumes").then((r) => {
      setResumes(r.data || []);
      if (r.data?.[0]) {
        setSelected(r.data[0].resume_id);
        if (r.data[0].ats_review) setData(r.data[0].ats_review);
        if (r.data[0].improved) setImproved(r.data[0].improved);
      }
    });
  }, []);

  // Reset improved + review when resume selection changes
  useEffect(() => {
    if (!selected) return;
    const r = resumes.find((x) => x.resume_id === selected);
    setData(r?.ats_review || null);
    setImproved(r?.improved || null);
  }, [selected, resumes]);

  const run = async () => {
    if (!selected) return toast.error("Select a resume");
    setBusy(true);
    setImproved(null);
    try {
      const r = await api.post(`/ats/review/${selected}`);
      setData(r.data);
      toast.success("Review ready");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const improve = async () => {
    if (!selected) return toast.error("Select a resume");
    if (!data) return toast.error("Run the ATS review first");
    setImproving(true);
    try {
      const r = await api.post(`/ats/improve/${selected}`);
      setImproved(r.data);
      toast.success("Improved resume ready");
      // smooth scroll to the new section
      setTimeout(() => {
        document.getElementById("improved-section")?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not generate improved resume");
    } finally {
      setImproving(false);
    }
  };

  const saveToVersions = async () => {
    if (!improved?.improved_resume_markdown) return;
    setSaving(true);
    try {
      await api.post(`/ats/save-improved/${selected}`, {
        content: improved.improved_resume_markdown,
        estimated_score: improved.estimated_score,
      });
      toast.success("Saved to Resume Versions (ATS-Friendly)");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const score = data?.score ?? null;
  const tone = score == null ? "#94a3b8" : score >= 70 ? "#10B981" : score >= 40 ? "#F59E0B" : "#EF4444";
  const newScore = improved?.estimated_score ?? null;
  const newTone = newScore == null ? "#94a3b8" : newScore >= 70 ? "#10B981" : newScore >= 40 ? "#F59E0B" : "#EF4444";
  const delta = (score != null && newScore != null) ? newScore - score : null;

  const activeResume = resumes.find((r) => r.resume_id === selected);
  const baseFilename = `cavilow-improved-${(activeResume?.filename || "resume").replace(/\.[^.]+$/, "")}`;

  return (
    <div className="space-y-8">
      <div className="fade-up">
        <p className="text-xs font-bold uppercase tracking-wider text-[#80FFEA]">ATS Review</p>
        <h1 className="font-display font-black text-4xl tracking-tight mt-1">How will robots read your resume?</h1>
        <p className="text-slate-500 mt-2 max-w-xl">Score 0–100 against ATS heuristics, with specific issues and fixes.</p>
      </div>

      <div className="glass p-6 fade-up-delay-1 flex flex-col sm:flex-row sm:items-end gap-4">
        <label className="block flex-1">
          <span className="text-xs font-bold uppercase tracking-wider text-slate-500">Choose resume</span>
          <select
            data-testid="ats-select-resume"
            value={selected}
            onChange={(e) => setSelected(e.target.value)}
            className="mt-1.5 w-full rounded-xl border border-white/10 focus:border-[#00E5FF] outline-none px-3 py-2.5 text-sm bg-white/5 text-white"
          >
            <option value="">— Select —</option>
            {resumes.map((r) => <option key={r.resume_id} value={r.resume_id}>{r.filename}</option>)}
          </select>
        </label>
        <button
          data-testid="ats-run"
          onClick={run}
          disabled={busy || !selected}
          className="btn-neon inline-flex items-center gap-2 disabled:opacity-60"
        >
          <RefreshCw size={16} /> {busy ? "Reviewing…" : "Run review"}
        </button>
      </div>

      {data && (
        <div className="grid lg:grid-cols-3 gap-6 fade-up-delay-2">
          <div className="glass p-6">
            <div className="text-xs font-bold uppercase tracking-wider text-slate-500">Overall score</div>
            <div className="mt-2 flex items-end gap-2">
              <span className="font-display font-black text-6xl leading-none" style={{ color: tone }}>{score}</span>
              <span className="text-sm text-slate-500 mb-1">/ 100</span>
            </div>
            <div className="mt-4 h-2 rounded-full bg-white/[0.06]">
              <div className="h-full rounded-full" style={{ width: `${score}%`, background: tone }} />
            </div>
            <div className="mt-6 grid grid-cols-2 gap-3">
              {[
                ["Format", data.formatting_score],
                ["Keywords", data.keyword_score],
                ["Impact", data.impact_score],
                ["Contact", data.contact_score],
              ].map(([k, v]) => (
                <div key={k} className="p-3 rounded-xl bg-white/5">
                  <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">{k}</div>
                  <div className="font-display font-black text-xl" style={{ color: tone }}>{v ?? "—"}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="glass p-6 lg:col-span-2">
            <div className="grid sm:grid-cols-2 gap-6">
              <Block icon={AlertTriangle} color="#EF4444" title="Issues found" items={data.issues} />
              <Block icon={Sparkles} color="var(--cavilo-blue)" title="Improvements" items={data.improvements} />
              <Block icon={ShieldCheck} color="#10B981" title="Strengths" items={data.strengths} />
            </div>
          </div>
        </div>
      )}

      {/* Upgrade ad — shown to free users after the ATS score is visible */}
      {data && !isPremium && <UpgradeAd feature="Improved resumes & more" testId="ats-upgrade-ad" />}

      {/* Generate Improved Resume CTA (premium only) */}
      {data && isPremium && (
        <div className="rounded-2xl ink-card p-6 sm:p-8 fade-up-delay-3 relative overflow-hidden">
          <div className="absolute -right-8 -top-8 w-44 h-44 rounded-full bg-[var(--cavilo-blue)]/25 blur-2xl pointer-events-none" />
          <div className="absolute -left-8 -bottom-8 w-44 h-44 rounded-full bg-[var(--cavilo-teal)]/25 blur-2xl pointer-events-none" />
          <div className="relative grid sm:grid-cols-[1fr_auto] items-center gap-5">
            <div>
              <div className="text-xs font-bold uppercase tracking-wider text-amber-300">Next step</div>
              <div className="font-display font-bold text-2xl mt-1">Apply every fix automatically.</div>
              <p className="text-sm text-slate-300 mt-2 max-w-2xl leading-relaxed">
                Cavilow will rewrite your resume using your existing content — improving ATS formatting, keywords, the summary, work bullets, and skills — without inventing anything.
              </p>
            </div>
            <button
              data-testid="ats-generate-improved"
              onClick={improve}
              disabled={improving}
              className="btn-neon inline-flex items-center gap-2 disabled:opacity-60 btn-press shrink-0"
            >
              <Wand2 size={16} /> {improving ? "Generating…" : "Generate Improved Resume"}
            </button>
          </div>
        </div>
      )}

      {/* Improved Resume Section */}
      {improved && (
        <section id="improved-section" className="space-y-5 fade-up-delay-3">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <span className="grid place-items-center w-10 h-10 rounded-2xl bg-[rgba(128,255,234,0.1)] text-emerald-600">
                <CheckCircle2 size={20} />
              </span>
              <div>
                <div className="text-xs font-bold uppercase tracking-wider text-[#80FFEA]">Improved</div>
                <h2 className="font-display font-bold text-2xl">Improved Resume Generated</h2>
              </div>
            </div>
            {newScore != null && (
              <div className="inline-flex items-center gap-3 glass px-4 py-2">
                <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Estimated ATS</div>
                <span className="font-display font-black text-3xl leading-none" style={{ color: newTone }}>{newScore}</span>
                <span className="text-xs text-slate-500">/ 100</span>
                {delta != null && delta > 0 && (
                  <span className="inline-flex items-center gap-0.5 text-xs font-bold text-emerald-600">
                    <ArrowUp size={12} /> +{delta}
                  </span>
                )}
              </div>
            )}
          </div>

          <div className="grid lg:grid-cols-3 gap-6">
            {/* Resume preview */}
            <div className="lg:col-span-2 glass overflow-hidden flex flex-col">
              <div className="flex items-center justify-between px-5 py-3 border-b border-white/8">
                <div className="font-display font-bold">Resume preview</div>
                <div className="flex flex-wrap gap-2">
                  <button
                    data-testid="ats-improved-pdf"
                    onClick={() => downloadFile(improved.improved_resume_markdown || "", baseFilename, "pdf")}
                    className="inline-flex items-center gap-1.5 rounded-lg bg-[var(--cavilo-ink)] text-white px-3 py-1.5 text-xs font-semibold hover:bg-[#1a2855] transition-colors"
                  >
                    <Download size={12} /> Download as PDF
                  </button>
                  <button
                    data-testid="ats-improved-docx"
                    onClick={() => downloadFile(improved.improved_resume_markdown || "", baseFilename, "docx")}
                    className="inline-flex items-center gap-1.5 rounded-lg bg-white/5 border border-white/10 px-3 py-1.5 text-xs font-semibold text-white hover:border-[#00E5FF] transition-colors"
                  >
                    <Download size={12} /> Download as DOCX
                  </button>
                  <button
                    data-testid="ats-improved-save"
                    onClick={saveToVersions}
                    disabled={saving}
                    className="inline-flex items-center gap-1.5 rounded-lg bg-[var(--cavilo-blue)] text-white px-3 py-1.5 text-xs font-semibold hover:bg-[var(--cavilo-blue-deep)] transition-colors disabled:opacity-60"
                  >
                    <Save size={12} /> {saving ? "Saving…" : "Save to Resume Versions"}
                  </button>
                </div>
              </div>
              <div className="p-5 max-h-[60vh] overflow-y-auto">
                <MarkdownPreview>{improved.improved_resume_markdown || ""}</MarkdownPreview>
              </div>
            </div>

            {/* Changes summary */}
            <div className="glass p-6">
              <div className="text-xs font-bold uppercase tracking-wider text-[#80FFEA] mb-3">What changed</div>
              <ul className="space-y-2">
                {(improved.changes_summary || []).map((c, i) => (
                  <li key={i} className="text-sm text-slate-200 leading-relaxed flex gap-2">
                    <Sparkles size={14} className="text-[#80FFEA] shrink-0 mt-0.5" />
                    <span>{c}</span>
                  </li>
                ))}
                {!(improved.changes_summary || []).length && <li className="text-sm text-slate-500">—</li>}
              </ul>
            </div>
          </div>

          {/* Disclaimer */}
          <div className="rounded-2xl bg-white/5 border border-white/8 p-4 flex items-start gap-3">
            <Info size={16} className="text-slate-500 shrink-0 mt-0.5" />
            <p className="text-xs text-slate-300 leading-relaxed">
              ATS scores are estimates and may vary across systems. Cavilow improves your resume structure, keywords, and clarity without adding false information.
            </p>
          </div>
        </section>
      )}
    </div>
  );
}

function Block({ icon: Icon, color, title, items }) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <span className="grid place-items-center w-7 h-7 rounded-lg" style={{ background: `${color}1A`, color }}>
          <Icon size={14} />
        </span>
        <div className="font-display font-bold text-sm uppercase tracking-wider text-slate-500">{title}</div>
      </div>
      <ul className="space-y-2">
        {(items || []).map((it, i) => (
          <li key={i} className="text-sm text-slate-200 leading-relaxed flex gap-2">
            <span className="text-slate-300">•</span><span>{it}</span>
          </li>
        ))}
        {!(items || []).length && <li className="text-sm text-slate-500">—</li>}
      </ul>
    </div>
  );
}
